<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import { Helper, debounce } from '@/mixin/helper'
import { GET_REPORT_SCHEDULER_HISTORY } from '@/services/api/report/report'
import { mapGetters } from 'vuex'

export default {
  page: {
    title: 'Riwayat Pengiriman Laporan',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
  },
  mixins: [Helper],
  apollo: {
    reportSchedulerHistory: {
      query: GET_REPORT_SCHEDULER_HISTORY,
      fetchPolicy: 'no-cache',
      variables() {
        return {
          filter: {
            pagination: {
              page: 1,
              show: 10,
            },
            type: this.hasAccess(['share-report:read'])
              ? ['reportScheduler', 'shareReport']
              : undefined,
            orderBy: [
              JSON.stringify({
                dateSentOn: 'desc',
              }),
              JSON.stringify({
                reportName: 'asc',
              }),
            ],
            excludeReportName: ['Email Digest', 'Pengingat Servis'],
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          this.totalRows = data.reportSchedulerHistory.reportSchedulerHistory.pagination.total
          this.reportSchedulerHistory = data.reportSchedulerHistory.reportSchedulerHistory
        }
      },
    },
  },
  data() {
    return {
      totalRows: 1,
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 25, 50, 100],
      filter: null,
      filterOn: [],
      filteredItems: [],
      reportSchedulerHistory: [],
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    fields() {
      return [
        {
          key: 'reportName',
          label: this.$t('report.title.reportType'),
          thClass: 'mc-table--170 fw-bold',
        },
        {
          key: 'deliveryType',
          label: this.$t('report.object.deliveryType'),
          thClass: 'mc-table--100 fw-bold',
        },
        {
          key: 'scheduleName',
          label: this.$t('report.title.scheduleName'),
          thClass: 'mc-table--170 fw-bold',
        },
        {
          key: 'recipients',
          label: this.$t('report.object.reportRecipient'),
          thClass: 'fw-bold',
        },
        {
          key: 'sentOn',
          label: this.$t('report.object.deliveryDate'),
          thClass: 'mc-table--170 fw-bold',
        },
        {
          key: 'status',
          label: 'Status',
          thClass: 'mc-table--100 fw-bold',
        },
      ]
    },
    variables() {
      return {
        type: this.hasAccess(['share-report:read'])
          ? ['reportScheduler', 'shareReport']
          : undefined,
        orderBy: [
          JSON.stringify({
            dateSentOn: 'desc',
          }),
          JSON.stringify({
            reportName: 'asc',
          }),
        ],
        excludeReportName: ['Email Digest', 'Pengingat Servis'],
      }
    },
  },
  methods: {
    searchData: debounce(function () {
      this.onFiltered()
    }, 500),
    onFiltered() {
      // Trigger pagination to update the number of buttons/pages due to filtering
      if (this.filter.length > 0 && this.filter.length < 3) return
      this.currentPage = 1
      this.$apollo.queries.reportSchedulerHistory.refetch({
        filter: {
          search: this.filter,
          pagination: {
            page: 1,
            show: this.perPage,
          },
          ...this.variables,
        },
      })
    },
    changePerPage(perPage) {
      this.perPage = perPage
      if (this.filter == null) {
        this.$apollo.queries.reportSchedulerHistory.refetch({
          filter: {
            pagination: {
              page: 1,
              show: this.perPage,
            },
            ...this.variables,
          },
        })
      } else {
        this.$apollo.queries.reportSchedulerHistory.refetch({
          filter: {
            search: this.filter,
            pagination: {
              page: 1,
              show: this.perPage,
            },
            ...this.variables,
          },
        })
      }
    },
    changePage(page) {
      this.currentPage = page
      if (this.filter == null) {
        this.$apollo.queries.reportSchedulerHistory.refetch({
          filter: {
            pagination: {
              page: this.currentPage,
              show: this.perPage,
            },
            ...this.variables,
          },
        })
      } else {
        this.$apollo.queries.reportSchedulerHistory.refetch({
          filter: {
            search: this.filter,
            pagination: {
              page: this.currentPage,
              show: this.perPage,
            },
            ...this.variables,
          },
        })
      }
    },
  },
}
</script>
<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader :title="$t('report.title.reportDeliveryHistory')" class="mc-page-three" />
      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <b-button
            @click="
              $router.push({
                name: 'Report',
                query: { domain: 'fleet' },
              })
            "
            variant="outline-primary"
            class="ms-3"
          >
            <i class="mc-fa icon-outline_arrow-left"></i>
            {{ $t('global.back') }}
          </b-button>
        </div>
      </div>
    </div>
    <div class="card">
      <div class="card-body">
        <div class="row">
          <!-- Search -->
          <div class="col-sm-12 col-md-12 d-flex flex-row-reverse">
            <div class="mc-table-view">
              <div id="tickets-table_filter" class="dataTables_filter text-md-end">
                <label class="d-inline-flex align-items-center mc-table-view__search">
                  <i class="mc-fa fas fa-search"></i>
                  <b-form-input
                    v-model="filter"
                    type="search"
                    :placeholder="$t('global.search') + '...'"
                    class="form-control ms-2"
                    @input="searchData"
                    ref="searchInput"
                  ></b-form-input>
                </label>
              </div>
            </div>
          </div>
          <!-- End search -->
        </div>
        <div class="card-body">
          <!-- Table -->
          <div class="table-responsive my-3 border border-1 mc-table--5">
            <b-table
              small
              show-empty
              :fields="fields"
              :items="reportSchedulerHistory.data"
              class="datatables"
              responsive="md"
              empty-table
              :empty-text="$t('global.emptyTable')"
              :empty-filtered-text="$t('global.emptyFilteredTable')"
              @filtered="onFiltered"
              :busy="$apollo.queries.reportSchedulerHistory.loading"
            >
              <template #cell(sentOn)="row">
                {{ momentDateShortMonth(row.value) }}
              </template>
              <template #cell(recipients)="data">
                <div>
                  {{ data.item.recipients.split(',').join(', ') }}
                </div>
              </template>
              <template #cell(scheduleName)="data">
                {{ data.item.type === 'RS' ? data.item.scheduleName : '-' }}
              </template>
              <template #cell(deliveryType)="data">
                {{
                  data.item.type === 'RS'
                    ? $t('report.object.schedule')
                    : $t('report.object.shareReport')
                }}
              </template>
              <template #cell(status)="data">
                <span class="mc-status mc-status--green" v-if="data.item.status == 'S'">
                  {{ $t('global.object.sent') }}
                </span>
                <span class="mc-status mc-status--red" v-else>
                  {{ $t('report.text.sentFailed') }}
                </span>
              </template>
              <template #table-busy>
                <div class="text-center text-danger my-2">
                  <b-spinner class="align-middle" />
                </div>
              </template>
            </b-table>
          </div>

          <div class="row">
            <div class="col-sm-12 col-md-6">
              <div id="tickets-table_length" class="dataTables_length">
                <label class="d-inline-flex align-items-center">
                  {{ $t('global.show') }}
                  <b-form-select
                    class="form-select form-select-sm"
                    v-model="perPage"
                    size="sm"
                    :options="pageOptions"
                    @input="changePerPage"
                  />
                  {{ $t('global.entry') }}.
                  <p>
                    {{ $t('global.show') }}&nbsp;<strong>{{
                      totalRows > 0 ? (currentPage - 1) * perPage + 1 : 0
                    }}</strong
                    >&nbsp;{{ $t('global.object.to') }}&nbsp;<strong>{{
                      Math.min(totalRows, currentPage * perPage)
                    }}</strong
                    >&nbsp;{{ $t('global.object.of') }}&nbsp;<strong>{{ totalRows }}</strong
                    >&nbsp;{{ $t('global.entry') }}.
                  </p>
                </label>
              </div>
            </div>
            <div class="col-sm-12 col-md-6">
              <div class="dataTables_paginate paging_simple_numbers float-end">
                <ul class="pagination pagination-rounded mb-0">
                  <!-- pagination -->
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="totalRows"
                    :per-page="perPage"
                    @change="changePage"
                  />
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
